<script lang="ts">
import type { InstantWinDraw } from '@/types'
import { type PropType, defineComponent } from 'vue'

import AppCarousel from './AppCarousel.vue'
import CarouselItem from './CarouselItem.vue'

export default defineComponent({
  components: {
    AppCarousel,
    CarouselItem,
  },
  props: {
    games: {
      type: Array as PropType<Array<InstantWinDraw>>,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    icon: {
      type: String,
      required: true,
    },
    remainingColumns: {
      type: Number,
      default: 0,
    },
  },
  emits: ['previousPage', 'nextPage'],
  data() {
    return {
      currentPageIndex: 0,
    }
  },
  computed: {
    mobilePaginatedGames(): Array<Array<InstantWinDraw>> {
      // Split games into pages of 4 (2x2 grid per page)
      const pages: Array<Array<InstantWinDraw>> = []
      const gamesPerPage = 4

      for (let i = 0; i < this.games.length; i += gamesPerPage) {
        pages.push(this.games.slice(i, i + gamesPerPage))
      }

      return pages.length > 0 ? pages : [[]]
    },
    isPreviousDisabled(): boolean {
      return this.currentPageIndex === 0
    },
    isNextDisabled(): boolean {
      return this.currentPageIndex === this.mobilePaginatedGames.length - 1
    },
    showNavigation(): boolean {
      return this.games.length > 4
    },
    emptyColumnsInGrid(): number {
      // Calculate how many columns to span in the 2-column grid
      // If remainingColumns is 1 or 3, card should span 1 column
      // If remainingColumns is 2, card should span 2 columns
      if (this.remainingColumns === 0) {
        return 0
      }
      return this.remainingColumns === 2 ? 2 : 1
    },
  },
  methods: {
    previousPage() {
      if (this.currentPageIndex > 0) {
        this.currentPageIndex--
        this.scrollToPage()
      }
      this.$emit('previousPage', this.currentPageIndex)
    },
    nextPage() {
      if (this.currentPageIndex < this.mobilePaginatedGames.length - 1) {
        this.currentPageIndex++
        this.scrollToPage()
      }
      this.$emit('nextPage', this.currentPageIndex)
    },
    scrollToPage() {
      const carousel = this.$refs.carousel as any
      carousel?.scrollbox?.scrollTo(this.currentPageIndex)
    },
  },
})
</script>

<template>
  <div>
    <div class="mb-3 flex items-center justify-between gap-3 sm:gap-4">
      <div class="flex items-center gap-1 sm:gap-4">
        <AppIcon :icon class="h-5 w-5 sm:h-8 sm:w-8" />
        <h2
          class="text-lg font-bold sm:text-2xl overflow-hidden text-ellipsis text-nowrap uppercase"
        >
          {{ title }}
        </h2>
      </div>

      <div
        v-if="showNavigation"
        class="flex items-center gap-2 px-3 @md/main:px-0"
      >
        <button
          class="button button-new button-new--icon-only touch-manipulation bg-slate-200"
          title="Previous Page"
          :disabled="isPreviousDisabled"
          @click="previousPage"
        >
          <AppIcon icon="ArrowLeft" size="xs" class="sm:h-6 sm:w-6" />
        </button>

        <button
          class="button button-new button-new--icon-only -scale-x-100 touch-manipulation bg-slate-200"
          title="Next Page"
          :disabled="isNextDisabled"
          @click="nextPage"
        >
          <AppIcon icon="ArrowLeft" size="xs" class="sm:h-6 sm:w-6" />
        </button>
      </div>
      <AppButton
        title="View all"
        class="button button-new leading-none whitespace-nowrap"
        :route="{ name: 'prizes', query: { switch: 'instant-wins' } }"
      >
        View All
      </AppButton>
    </div>

    <AppCarousel
      ref="carousel"
      :show-navigation="false"
      frame-class="mobile-game-carousel"
    >
      <CarouselItem
        v-for="(page, pageIndex) in mobilePaginatedGames"
        :key="`page-${pageIndex}`"
        class="w-full"
      >
        <div class="grid grid-cols-2 gap-3">
          <slot :games="page" :page-index="pageIndex" :page="page" />
          <slot
            v-if="
              pageIndex === mobilePaginatedGames.length - 1 &&
              remainingColumns > 0
            "
            name="empty"
            :empty-slots="emptyColumnsInGrid"
          ></slot>
        </div>
      </CarouselItem>
    </AppCarousel>
  </div>
</template>
